import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { DecoratorNode } from "lexical";
import * as React from "react";
import { Suspense } from "react";

import { Caption, Image } from "../../../../../../../shared/block-editor-data/types";
import { registerNodeCheck } from "../BlockContainer/Caption/is";
import { createBlankCaption } from "../utils";

const ImageComponent = React.lazy(
  // @ts-ignore
  () => import("./ImageComponent"),
);

function convertImageElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src } = domNode;
    const caption = createBlankCaption();

    const node = $createImageNode({
      altText,
      src,
      type: "image",
      version: 1,
      indent: 0,
      caption: caption,
    });
    return { node };
  }
  return null;
}

export type SerializedImageNode = Spread<Image, SerializedLexicalNode>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __caption: Caption;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__caption);
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, src, caption } = serializedNode;

    const node = $createImageNode({
      altText,
      src,
      type: "image",
      version: 1,
      indent: 0,
      caption: caption,
    });

    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    element.setAttribute("alt", this.__altText);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(src: string, altText: string, caption: Caption, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__caption = caption;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      src: this.getSrc(),
      type: "image",
      version: 1,
      indent: 0,
      caption: this.__caption,
    };
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateCaptionJSON(json: string): void {
    const self = this.getWritable();
    self.__caption = { ...self.__caption, json };
  }
  updateImageNode(srcURL: string, alt: string, key?: string): void {
    const self = this.getWritable();
    self.__altText = alt;
    self.__src = srcURL;
  }
  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  isInline() {
    return false;
  }

  decorate(_: LexicalEditor, config: EditorConfig): JSX.Element {
    return (
      <Suspense>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          caption={this.__caption}
          nodeKey={this.getKey()}
          theme={config.theme}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({ altText, src, caption }: Image): ImageNode {
  return new ImageNode(src, altText, caption);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

registerNodeCheck($isImageNode);
